import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Landmark, Plus } from "lucide-react";
import AdminListPage from "./AdminListPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "", email: "", amount_eur: "", reference: "",
  billing_nif: "", billing_address: "", company: "", billing_company_nif: "",
  phone: "", message: "",
};

const Field = ({ label, value, onChange, testid, required, type = "text", placeholder, hint }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">
      {label}{required ? " *" : ""}
    </label>
    <input
      type={type} value={value} placeholder={placeholder || ""} required={!!required}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
      data-testid={testid}
    />
    {hint && <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">{hint}</p>}
  </div>
);

// Dialog: admin records a donation received by bank transfer. The backend
// marks it paid, generates the Declaração de Donativo PDF and emails it.
function ManualTransferDialog({ open, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/members`, { withCredentials: true })
      .then((r) => setMembers((r.data.items || []).filter((m) => m.status === "approved")))
      .catch((e) => console.warn("[AdminDonations] members load:", e?.message));
  }, [open]);

  if (!open) return null;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const pickMember = (id) => {
    setMemberId(id);
    if (!id) { setForm(EMPTY); return; }
    const m = members.find((x) => x.id === id);
    if (!m) return;
    setForm((f) => ({
      ...f,
      full_name: m.name || "",
      email: m.email || "",
      phone: m.phone || "",
      billing_nif: m.billing_nif || "",
      billing_address: m.billing_address || "",
      company: m.company || "",
      billing_company_nif: m.billing_company_nif || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(String(form.amount_eur).replace(",", "."));
    if (!amount || amount <= 0) { toast.error("Indique um valor válido."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/donations/manual-transfer`, {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        amount_eur: amount,
        reference: form.reference.trim() || null,
        billing_nif: form.billing_nif.trim() || null,
        billing_address: form.billing_address.trim() || null,
        company: form.company.trim() || null,
        billing_company_nif: form.billing_company_nif.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim() || null,
      }, { withCredentials: true });
      toast.success("Donativo registado · Declaração enviada por email ao doador.", { duration: 7000 });
      setForm(EMPTY);
      setMemberId("");
      onDone();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Erro a registar o donativo.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" data-testid="manual-donation-dialog">
      <form onSubmit={submit} className="bg-white max-w-2xl w-full p-8 border border-[hsl(var(--border))] max-h-[92vh] overflow-y-auto">
        <div className="overline">Transferência bancária</div>
        <h3 className="display-heading text-2xl mt-3">Registar donativo recebido.</h3>
        <span className="accent-rule mt-5 block" />
        <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
          Ao registar, é emitida automaticamente a <strong>Declaração de Donativo</strong> (numeração sequencial,
          válida para efeitos fiscais) e enviada por email ao doador — com cópia para geral@ciees.pt.
        </p>
        <div className="mt-6">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">
            Selecionar sócio (opcional)
          </label>
          <select
            value={memberId}
            onChange={(e) => pickMember(e.target.value)}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
            data-testid="md-member-select"
          >
            <option value="">— Doador externo (preencher manualmente) —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.name || m.email} ({m.email})</option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
            Ao escolher um sócio, os dados (nome, email, NIF, morada fiscal…) são preenchidos a partir do perfil — pode ajustá-los antes de registar.
          </p>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <Field label="Nome do doador" value={form.full_name} onChange={set("full_name")} testid="md-name" required />
          <Field label="Email do doador" value={form.email} onChange={set("email")} testid="md-email" required type="email" />
          <Field label="Valor (€)" value={form.amount_eur} onChange={set("amount_eur")} testid="md-amount" required placeholder="250.00" />
          <Field label="Referência da transferência" value={form.reference} onChange={set("reference")} testid="md-reference" placeholder="Nº operação / data" hint="Para rastreio interno." />
          <Field label="NIF (pessoa singular)" value={form.billing_nif} onChange={set("billing_nif")} testid="md-nif" hint="Necessário para dedução fiscal do doador." />
          <Field label="Telefone" value={form.phone} onChange={set("phone")} testid="md-phone" />
          <Field label="Empresa (se aplicável)" value={form.company} onChange={set("company")} testid="md-company" />
          <Field label="NIF da empresa" value={form.billing_company_nif} onChange={set("billing_company_nif")} testid="md-cnif" hint="Se preenchido, a Declaração é emitida em nome da empresa." />
        </div>
        <div className="mt-4">
          <Field label="Morada fiscal" value={form.billing_address} onChange={set("billing_address")} testid="md-address" />
        </div>
        <div className="mt-4">
          <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Nota interna (opcional)</label>
          <textarea value={form.message} onChange={(e) => set("message")(e.target.value)} rows={2}
            className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
            data-testid="md-message" />
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <button type="submit" disabled={busy} className="btn-primary disabled:opacity-60" data-testid="md-submit">
            <Landmark className="h-4 w-4" /> {busy ? "A registar…" : "Registar e emitir Declaração"}
          </button>
          <button type="button" onClick={onClose} className="ml-auto text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="md-close">
            Fechar
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDonations() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-14" data-testid="admin-donations-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Donativos</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Donativos recebidos.</h1>
          <span className="accent-rule mt-6 block" />
        </div>
        <button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="open-manual-donation">
          <Plus className="h-4 w-4" /> Registar transferência
        </button>
      </div>
      <AdminListPage
        key={refreshKey}
        embedded
        overline="Donativos" title="Donativos recebidos"
        type="donations" endpoint="/admin/donations"
        csvUrl="/admin/donations.csv"
        defaultEmpty="Ainda sem donativos."
      />
      <ManualTransferDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onDone={() => { setDialogOpen(false); setRefreshKey((k) => k + 1); }}
      />
    </div>
  );
}
